// Search normalization is deliberately separate from account and identity matching.
export function normalizeSearch(value) {
  return String(value || "").normalize("NFKC").toLowerCase()
    .replace(/[\u0610-\u061a\u064b-\u065f\u0670\u06d6-\u06ed\u0640]/g, "")
    .replace(/[أإآٱٲ]/g, "ا").replace(/ى|ئ/g, "ي").replace(/ؤ/g, "و").replace(/ة/g, "ه")
    .replace(/[^\p{L}\p{N}\s]/gu, " ").replace(/\s+/g, " ").trim();
}

const wordsOf = text => text.split(" ").map(word => word.length > 3 ? word.replace(/^(?:وال|بال|كال|فال|لل|ال)/, "") : word).join(" ");

export function editDistance(a, b) {
  let prevprev, prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      let value = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) value = Math.min(value, prevprev[j - 2] + 1);
      row.push(value);
    }
    prevprev = prev; prev = row;
  }
  return prev[b.length];
}

export function searchScore(value, query) {
  const raw = normalizeSearch(value), term = normalizeSearch(String(query || "").slice(0, 160));
  if (!term) return 1;
  if (!raw) return 0;
  if (raw === term) return 1000;
  const text = wordsOf(raw), needle = wordsOf(term);
  if (!needle) return 0;
  if (text === needle) return 960;
  if (text.startsWith(needle)) return 920 - Math.min(30, text.length - needle.length);
  if (text.includes(needle)) return 870 - Math.min(30, text.length - needle.length);
  const words = text.split(" "), tokens = needle.split(" "), scores = [];
  for (const token of tokens) {
    let best = 0;
    for (const word of words) {
      if (token === word) { best = 100; break; }
      if (token.length >= 2 && word.startsWith(token)) best = Math.max(best, 85);
      if (Math.min(token.length, word.length) >= 4) {
        const limit = Math.max(token.length, word.length) >= 7 ? 2 : 1;
        if (Math.abs(token.length - word.length) <= limit) {
          const distance = editDistance(token, word);
          if (distance <= limit) best = Math.max(best, 76 - distance * 12);
        }
      }
    }
    if (!best) return 0;
    scores.push(best);
  }
  return 500 + scores.reduce((n, v) => n + v, 0) / scores.length - Math.min(30, Math.max(0, words.length - tokens.length));
}

export function rankedSearch(items, query, getText = item => item.name || item.title || "") {
  if (!String(query || "").trim()) return items;
  return items.map((item, index) => {
    const values = [].concat(getText(item)).map(v => String(v || ""));
    const score = Math.max(...values.map(value => searchScore(value, query)), searchScore(values.join(" "), query));
    return { item, index, score };
  }).filter(row => row.score > 0).sort((a, b) => b.score - a.score || a.index - b.index).map(row => row.item);
}
